import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useTranslation } from "react-i18next";
import { api } from "../api/axios";
import { StatusCard } from "./marketing/MarketingParts";

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | success | pending | error
  const [messageKey, setMessageKey] = useState("");

  // Get transaction ID from URL params (we send it in success_url)
  // SmartPay might also append its own params
  const transactionId = params.get("moreinfo1") || params.get("transaction_id") || params.get("id");

  useEffect(() => {
    // If we have a transaction ID from SmartPay, check its status
    if (transactionId) {
      checkTransactionStatus(transactionId);
    } else {
      // No transaction ID - just show success message
      setStatus("success");
      setMessageKey("mkp_msg_default");
    }
  }, [transactionId]);

  const checkTransactionStatus = async (txId) => {
    try {
      const { data } = await api.get(`/api/payment/transaction/${encodeURIComponent(txId)}`);
      const txStatus = String(data?.transaction?.status || "").toLowerCase();

      if (txStatus === "completed") {
        setStatus("success");
        setMessageKey("mkp_msg_completed");
      } else if (txStatus === "pending") {
        setStatus("pending");
        setMessageKey("mkp_msg_pending");
      } else {
        setStatus("success");
        setMessageKey("mkp_msg_done");
      }
    } catch (err) {
      // Transaction not found yet (webhook hasn't processed) - that's OK
      console.log("Transaction check:", err?.response?.data);
      setStatus("success");
      setMessageKey("mkp_msg_soon");
    }
  };

  if (status === "checking") {
    return (
      <StatusCard>
        <Box aria-live="polite" sx={{ display: "grid", justifyItems: "center", gap: 2, py: 2 }}>
          <CircularProgress aria-hidden="true" />
          <Typography>{t("mkp_checking")}</Typography>
        </Box>
      </StatusCard>
    );
  }

  return (
    <StatusCard
      icon={<CheckCircleRoundedIcon />}
      tone="success"
      title={t("mkp_success_title")}
      actions={
        <>
          <Button variant="contained" size="large" onClick={() => nav("/overview")}>
            {t("mkp_back_dash")}
          </Button>
          <Button variant="outlined" size="large" onClick={() => nav("/sites")}>
            {t("mkp_my_sites")}
          </Button>
        </>
      }
    >
      <Alert severity={status === "success" ? "success" : "info"}>{t(messageKey)}</Alert>

      {transactionId && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
          {t("mkp_tx")}{" "}
          <Box component="span" sx={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", direction: "ltr", unicodeBidi: "isolate" }}>
            {transactionId}
          </Box>
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
        {t("mkp_email_note")}
      </Typography>
    </StatusCard>
  );
}
